import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ForkKnife, BowlFood } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function FoodCourtPublic() {
  const [board, setBoard] = useState(null);
  const [good, setGood] = useState(null);

  useEffect(() => {
    api.get("/cafeteria/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ outlets: [], specials: [] }));
    api.get("/cafeteria/public/whats-good").then(({ data }) => setGood(data)).catch(() => {});
  }, []);

  useSeo({
    title: "The food court today | Unity & Utility",
    description: "What is on across the food court today — the chains, the local stall, our own canteen counter, the yard food truck and the machines, with prices and dietary tags.",
    path: "/food-court",
    image: board?.picture ? `${BACKEND}${board.picture}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Food court", "/food-court"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="food-court-page">
        <Crumbs items={[["Home", "/"], ["Food court", "/food-court"]]} />
        <p className="overline text-[#B3241B]">On site</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <ForkKnife size={38} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          The food court today
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700">
          The chains, the local stall, our own canteen counter, the yard food truck and the machines.
          Pictures are redrawn every 24 hours, so this board always shows today.
        </p>
        <div className="mt-5">
          <ShareBar url={`${BACKEND}/api/share/food-court`} text="What's on the food court today"
            testid="food-court-share" instagram />
        </div>

        {board?.poster?.url && (
          <div className="mt-8 border border-border bg-white" data-testid="food-court-poster">
            <img src={`${BACKEND}${board.poster.url}`} alt="Today's canteen board"
              className="w-full object-cover" />
            <div className="flex flex-wrap items-center justify-between gap-2 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                Today's board · {board.poster.day}
              </p>
              <ShareBar url={`${BACKEND}/api/share/food-court`} text="Today's board at the canteen"
                testid="food-court-poster-share" compact instagram />
            </div>
          </div>
        )}

        {good?.text && (
          <div className="mt-8 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3" data-testid="food-court-whats-good">
            <p className="overline text-[#002FA7]">What's good today</p>
            <p className="mt-1 whitespace-pre-line text-base leading-relaxed text-[#0F172A]">{good.text}</p>
            <p className="mt-2 text-[11px] uppercase tracking-wide text-slate-500">Read by {good.engine_label}</p>
          </div>
        )}

        <div className="mt-8 border-l-2 border-[#0B5C2E] bg-[#ECF7F0] px-4 py-4" data-testid="food-court-fridge-promo">
          <p className="overline text-[#0B5C2E]">Also on site</p>
          <h2 className="font-display mt-1 flex items-center gap-2 text-base font-bold text-[#0F172A] md:text-lg">
            <BowlFood size={20} weight="duotone" className="shrink-0 text-[#0B5C2E]" />
            The workplace fridge
          </h2>
          <p className="mt-2 text-base leading-relaxed text-slate-700">
            Fresh cut fruit salad, sandwiches, yoghurts and snacks from Jewel-Osco, Tony's Fresh Market,
            Mariano's and other local stores, dropped into the shared fridge on a weekly schedule and free
            to employees. Use-by dates, an "eat me today" list and a waste log are all kept on the board.
          </p>
          <Link to="/fridge" data-testid="food-court-fridge-link"
            className="tap-safe mt-3 inline-flex items-center text-sm font-semibold text-[#0B5C2E] underline">
            See what's in the fridge today
          </Link>
        </div>

        {board?.specials?.length > 0 && (
          <div className="mt-10" data-testid="food-court-specials">
            <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">Today's specials</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {board.specials.map((s, i) => (
                <Link key={s.id} to={`/food-court/${s.id}`} data-testid={`food-court-special-${i}`}
                  className="border border-border bg-white transition-colors duration-150 hover:border-[#002FA7]">
                  {s.image_url && <img src={`${BACKEND}${s.image_url}`} alt={s.name} className="h-44 w-full object-cover" />}
                  <div className="p-3">
                    <p className="font-semibold text-[#0F172A]">{s.name}</p>
                    <p className="text-xs text-slate-500">{s.symbol}{s.price} · {LABEL(s.category)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {board?.outlets?.map((o, oi) => (
          <div key={o.id} className="mt-10" data-testid={`food-court-outlet-${oi}`}>
            <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">{o.name}</h2>
            <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
              {o.kind_label}{o.note ? ` · ${o.note}` : ""}
            </p>
            {o.delivery?.length > 0 && (
              <div className="scroll-x mt-2 flex gap-2 overflow-x-auto">
                {o.delivery.map((d) => (
                  <a key={d.key} href={d.url} target="_blank" rel="noreferrer"
                    data-testid={`food-court-delivery-${oi}-${d.key}`}
                    className="tap-safe shrink-0 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
                    {d.label}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {o.items.map((item, i) => (
                <div key={item.id} data-testid={`food-court-item-${oi}-${i}`} className="border border-border bg-white">
                  {item.image_url && (
                    <Link to={`/food-court/${item.id}`}>
                      <img src={`${BACKEND}${item.image_url}`} alt={item.name} className="h-40 w-full object-cover" />
                    </Link>
                  )}
                  <div className="p-3">
                    <Link to={`/food-court/${item.id}`} className="font-semibold text-[#0F172A] hover:text-[#002FA7]">
                      {item.name}
                    </Link>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {item.symbol}{item.price} · {LABEL(item.category)}
                      {item.dietary.length > 0 ? ` · ${item.dietary.map(LABEL).join(", ")}` : ""}
                    </p>
                    <div className="mt-2">
                      <ShareBar url={`${BACKEND}/api/share/cafeteria/${item.id}`} text={item.name}
                        testid={`food-court-share-${oi}-${i}`} compact instagram />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PublicShell>
  );
}

export function FoodItemPublic() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/cafeteria/public/item/${id}`)
      .then(({ data }) => setItem(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Not on today"));
  }, [id]);

  useSeo({
    title: item ? `${item.name} | Food court | Unity & Utility` : "Food court | Unity & Utility",
    description: item ? `${item.name} at ${item.outlet_name} — ${item.currency} ${item.price}.` : "On the food court board.",
    path: `/food-court/${id}`,
    image: item?.image_url ? `${BACKEND}${item.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Food court", "/food-court"], [item?.name || "Item", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="food-item-page">
        <Crumbs items={[["Home", "/"], ["Food court", "/food-court"], [item?.name || "Item", null]]} />
        {error && <p data-testid="food-item-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {item && (
          <>
            <p className="overline text-[#B3241B]">{item.outlet_name} · {LABEL(item.category)}</p>
            <h1 data-testid="food-item-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              {item.name}
            </h1>
            {item.image_url && (
              <img data-testid="food-item-image" src={`${BACKEND}${item.image_url}`} alt={item.name}
                className="mt-6 w-full border border-border object-cover" />
            )}
            <p data-testid="food-item-price" className="mt-5 text-2xl font-bold text-[#0B5C2E]">
              {item.symbol}{item.price}
              {item.dietary.length > 0 && (
                <span className="ml-2 align-middle text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {item.dietary.map(LABEL).join(" · ")}
                </span>
              )}
            </p>
            {item.outlet_note && <p className="mt-3 text-base text-slate-700">{item.outlet_note}</p>}
            <div className="mt-6">
              <ShareBar url={`${BACKEND}/api/share/cafeteria/${id}`} text={item.name}
                testid="food-item-share" instagram />
            </div>
            <Link to="/food-court" data-testid="food-item-back"
              className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
              See everything on the food court today
            </Link>
          </>
        )}
      </div>
    </PublicShell>
  );
}
